// --- Canonical attribution persistence (first touch + last touch + click IDs) ---
(function () {
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const CLICK_ID_KEYS = [
    'gclid',
    'dclid',
    'gbraid',
    'wbraid',
    'msclkid',
    'fbclid',
    'ttclid',
    'twclid',
    'li_fat_id',
    'pin_clickid',
    'rdt_cid'
  ];
  const STORAGE_KEY = 'stern_attribution_v2';
  const COOKIE_KEY = 'stern_attribution_v2';
  const COOKIE_DOMAIN = '.sternpinball.com';
  const MAX_COOKIE_AGE_SECONDS = 90 * 24 * 60 * 60;
  const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  const LAST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const SESSION_TTL_MS = 30 * 60 * 1000;
  const EMPTY_TOUCH = {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    gclid: '',
    dclid: '',
    gbraid: '',
    wbraid: '',
    msclkid: '',
    fbclid: '',
    ttclid: '',
    twclid: '',
    li_fat_id: '',
    pin_clickid: '',
    rdt_cid: '',
    referrer_hostname: '',
    landing_page: '',
    landing_ts: 0
  };
  const INTERNAL_HOSTS = [
    'sternpinball.com',
    'shop.sternpinball.com',
    'staging-sternpinball.kinsta.cloud'
  ];

  function sanitizeString(value, maxLen) {
    const maxLength = typeof maxLen === 'number' ? maxLen : 256;
    if (value == null) return '';
    return String(value).trim().slice(0, maxLength);
  }

  function sanitizeTimestamp(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 0;
  }

  function hasAnyValue(values) {
    if (!values || typeof values !== 'object') return false;
    return Object.keys(values).some((key) => sanitizeString(values[key]).length > 0);
  }

  function getReferrerHostname() {
    if (!document.referrer) return '';
    try {
      return sanitizeString(new URL(document.referrer).hostname.toLowerCase(), 128);
    } catch (e) {
      return '';
    }
  }

  function isInternalHost(hostname) {
    const host = sanitizeString(hostname, 128).toLowerCase();
    if (!host) return false;
    return INTERNAL_HOSTS.some((entry) => host === entry || host.endsWith('.' + entry));
  }

  function getLandingPage() {
    const path = sanitizeString(window.location.pathname || '/', 512) || '/';
    const search = sanitizeString(window.location.search || '', 512);
    return sanitizeString(path + search, 768);
  }

  function readQueryParams(keys) {
    const urlParams = new URLSearchParams(window.location.search);
    const values = {};
    keys.forEach((key) => {
      const value = sanitizeString(urlParams.get(key));
      if (value) values[key] = value;
    });
    return values;
  }

  function readCookieByName(name) {
    try {
      const pattern = '(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)';
      const match = document.cookie.match(new RegExp(pattern));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function writeCookie(name, value) {
    try {
      const encodedValue = encodeURIComponent(value);
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = [
        name + '=' + encodedValue,
        'path=/',
        'max-age=' + MAX_COOKIE_AGE_SECONDS,
        'domain=' + COOKIE_DOMAIN,
        'SameSite=Lax' + secure
      ].join('; ');
    } catch (e) {}
  }

  function clearCookie(name) {
    try {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = [
        name + '=',
        'path=/',
        'max-age=0',
        'domain=' + COOKIE_DOMAIN,
        'SameSite=Lax' + secure
      ].join('; ');
    } catch (e) {}
  }

  function readClickIdsFromUrlOrCookie() {
    const clickIds = readQueryParams(CLICK_ID_KEYS);
    if (!clickIds.rdt_cid) {
      const rdtCid = sanitizeString(readCookieByName('rdt_cid'));
      if (rdtCid) clickIds.rdt_cid = rdtCid;
    }
    return clickIds;
  }

  function shouldPersistAttribution() {
    const consentConfig = window.__sternConsentConfig || {};
    return consentConfig.allowEssentialAttributionStorage !== false;
  }

  function hasAnalyticsConsent() {
    try {
      if (window.CookieYes && window.CookieYes.consent) {
        return window.CookieYes.consent.analytics === true;
      }
    } catch (e) {}
    try {
      if (window.cookieyes && window.cookieyes.consent) {
        return window.cookieyes.consent.analytics === true;
      }
    } catch (e) {}
    return false;
  }

  function createVisitorId() {
    const seed = Math.random().toString(36).slice(2, 10);
    return 'sid_' + Date.now().toString(36) + '_' + seed;
  }

  function sanitizeTouch(rawTouch) {
    const touch = Object.assign({}, EMPTY_TOUCH);
    const incoming = rawTouch || {};
    Object.keys(touch).forEach((key) => {
      if (key === 'landing_ts') {
        touch[key] = sanitizeTimestamp(incoming[key]);
      } else {
        const maxLen = key === 'landing_page' ? 768 : 256;
        touch[key] = sanitizeString(incoming[key], maxLen);
      }
    });
    return touch;
  }

  function normalizeCanonical(rawCanonical) {
    const now = Date.now();
    const canonical = rawCanonical && typeof rawCanonical === 'object' ? rawCanonical : {};

    const normalized = {
      schema_version: 2,
      storage_mode: shouldPersistAttribution() ? 'persistent' : 'memory_only',
      created_at: sanitizeTimestamp(canonical.created_at) || now,
      updated_at: sanitizeTimestamp(canonical.updated_at) || now,
      first_touch_at: sanitizeTimestamp(canonical.first_touch_at),
      last_touch_at: sanitizeTimestamp(canonical.last_touch_at),
      session_start_at: sanitizeTimestamp(canonical.session_start_at),
      session_last_at: sanitizeTimestamp(canonical.session_last_at),
      first_touch: sanitizeTouch(canonical.first_touch),
      last_touch: sanitizeTouch(canonical.last_touch),
      visitor_id: sanitizeString(canonical.visitor_id, 80),
      source_origin: sanitizeString(canonical.source_origin, 32)
    };

    if (normalized.first_touch_at && now - normalized.first_touch_at > FIRST_TOUCH_TTL_MS) {
      normalized.first_touch_at = 0;
      normalized.first_touch = sanitizeTouch(null);
    }

    if (normalized.last_touch_at && now - normalized.last_touch_at > LAST_TOUCH_TTL_MS) {
      normalized.last_touch_at = 0;
      normalized.last_touch = sanitizeTouch(null);
    }

    if (!normalized.session_last_at || now - normalized.session_last_at > SESSION_TTL_MS) {
      normalized.session_start_at = now;
    }
    normalized.session_last_at = now;
    normalized.updated_at = now;

    if (!normalized.visitor_id && hasAnalyticsConsent()) {
      normalized.visitor_id = createVisitorId();
    }

    return normalized;
  }

  function touchSignature(touch) {
    const safeTouch = sanitizeTouch(touch);
    const keys = UTM_KEYS.concat(CLICK_ID_KEYS);
    return keys.map((key) => safeTouch[key] || '').join('|');
  }

  function shouldUpdateLastTouch(previousTouch, incomingTouch) {
    const previousSignature = touchSignature(previousTouch);
    const incomingSignature = touchSignature(incomingTouch);
    if (!incomingSignature.replace(/\|/g, '')) return false;
    return previousSignature !== incomingSignature;
  }

  function buildIncomingTouch(now) {
    const utmValues = readQueryParams(UTM_KEYS);
    const clickIds = readClickIdsFromUrlOrCookie();
    const referrerHostname = getReferrerHostname();

    const touch = sanitizeTouch(Object.assign({}, utmValues, clickIds, {
      referrer_hostname: referrerHostname,
      landing_page: getLandingPage(),
      landing_ts: now
    }));

    const hasCampaignSignal = hasAnyValue(utmValues) || hasAnyValue(clickIds);
    if (hasCampaignSignal) {
      return { touch, isQualifying: true, sourceOrigin: 'shopify' };
    }

    if (referrerHostname && !isInternalHost(referrerHostname)) {
      // External referrer without UTM params: keep referrer_hostname (already in touch)
      // but do NOT synthesize utm_source/medium/campaign. Blank is preferred over fake values.
      return { touch, isQualifying: true, sourceOrigin: 'shopify' };
    }

    return { touch, isQualifying: false, sourceOrigin: 'shopify' };
  }

  function readLegacyAttributionFallback() {
    try {
      const raw = localStorage.getItem('stern_utm_v1');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.utm || !parsed.ts) return null;
      const ts = sanitizeTimestamp(parsed.ts);
      if (!ts) return null;
      const touch = sanitizeTouch(Object.assign({}, parsed.utm, { landing_ts: ts }));
      if (isLegacyWpFallbackTouch(touch)) {
        try {
          localStorage.removeItem('stern_utm_v1');
        } catch (e) {}
        return null;
      }
      return {
        schema_version: 2,
        created_at: ts,
        updated_at: Date.now(),
        first_touch_at: ts,
        last_touch_at: ts,
        first_touch: touch,
        last_touch: touch
      };
    } catch (e) {
      return null;
    }
  }

  function isSyntheticReferralTouch(rawTouch) {
    const touch = sanitizeTouch(rawTouch);
    const medium = sanitizeString(touch.utm_medium, 80).toLowerCase();
    const campaign = sanitizeString(touch.utm_campaign, 120).toLowerCase();
    if (medium !== 'referral' || campaign !== 'referral') return false;
    if (sanitizeString(touch.utm_term) || sanitizeString(touch.utm_content)) return false;
    if (CLICK_ID_KEYS.some((key) => sanitizeString(touch[key]))) return false;
    return true;
  }

  function cleanSyntheticReferralTouch(rawTouch) {
    const touch = sanitizeTouch(rawTouch);
    touch.utm_source = '';
    touch.utm_medium = '';
    touch.utm_campaign = '';
    return touch;
  }

  function cleanupSyntheticReferralCanonical(rawCanonical) {
    if (!rawCanonical || typeof rawCanonical !== 'object') {
      return { canonical: rawCanonical, didClean: false };
    }
    const canonical = Object.assign({}, rawCanonical);
    let didClean = false;
    if (isSyntheticReferralTouch(canonical.first_touch)) {
      canonical.first_touch = cleanSyntheticReferralTouch(canonical.first_touch);
      didClean = true;
    }
    if (isSyntheticReferralTouch(canonical.last_touch)) {
      canonical.last_touch = cleanSyntheticReferralTouch(canonical.last_touch);
      didClean = true;
    }
    return { canonical, didClean };
  }

  function isLegacyWpFallbackTouch(rawTouch) {
    const touch = sanitizeTouch(rawTouch);
    const source = sanitizeString(touch.utm_source, 80).toLowerCase();
    const medium = sanitizeString(touch.utm_medium, 80).toLowerCase();
    const campaign = sanitizeString(touch.utm_campaign, 120).toLowerCase();
    if (source !== 'wp' || medium !== 'referral' || campaign !== 'wp_to_shop') return false;
    if (sanitizeString(touch.utm_term) || sanitizeString(touch.utm_content)) return false;
    if (CLICK_ID_KEYS.some((key) => sanitizeString(touch[key]))) return false;
    const refHost = sanitizeString(touch.referrer_hostname, 128).toLowerCase();
    if (refHost && !isInternalHost(refHost)) return false;
    return true;
  }

  function cleanupLegacyWpFallbackCanonical(rawCanonical) {
    if (!rawCanonical || typeof rawCanonical !== 'object') {
      return { canonical: rawCanonical, didClean: false };
    }

    const canonical = Object.assign({}, rawCanonical);
    const firstTouch = sanitizeTouch(canonical.first_touch);
    const lastTouch = sanitizeTouch(canonical.last_touch);
    const firstFallback = isLegacyWpFallbackTouch(firstTouch);
    const lastFallback = isLegacyWpFallbackTouch(lastTouch);

    if (!firstFallback && !lastFallback) {
      return { canonical: rawCanonical, didClean: false };
    }

    let nextFirst = firstFallback ? null : firstTouch;
    let nextLast = lastFallback ? null : lastTouch;

    if (!nextFirst && nextLast && hasAnyValue(nextLast)) {
      nextFirst = sanitizeTouch(nextLast);
    }
    if (!nextLast && nextFirst && hasAnyValue(nextFirst)) {
      nextLast = sanitizeTouch(nextFirst);
    }

    if (!nextFirst && !nextLast) {
      return { canonical: null, didClean: true };
    }

    canonical.first_touch = nextFirst || sanitizeTouch(null);
    canonical.last_touch = nextLast || sanitizeTouch(null);
    canonical.first_touch_at = nextFirst
      ? (sanitizeTimestamp(canonical.first_touch_at) || sanitizeTimestamp(canonical.last_touch_at) || Date.now())
      : 0;
    canonical.last_touch_at = nextLast
      ? (sanitizeTimestamp(canonical.last_touch_at) || sanitizeTimestamp(canonical.first_touch_at) || Date.now())
      : 0;

    return { canonical, didClean: true };
  }

  function loadCanonicalAttribution() {
    if (!shouldPersistAttribution()) return normalizeCanonical(null);
    let parsed = null;
    try {
      const rawLocal = localStorage.getItem(STORAGE_KEY);
      if (rawLocal) parsed = JSON.parse(rawLocal);
    } catch (e) {}
    if (!parsed) {
      try {
        const rawCookie = readCookieByName(COOKIE_KEY);
        if (rawCookie) parsed = JSON.parse(rawCookie);
      } catch (e) {}
    }
    if (!parsed) {
      parsed = readLegacyAttributionFallback();
    }

    const cleanupResult = cleanupLegacyWpFallbackCanonical(parsed);
    if (cleanupResult.didClean && !cleanupResult.canonical) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
      clearCookie(COOKIE_KEY);
    }
    parsed = cleanupResult.canonical;

    const referralCleanup = cleanupSyntheticReferralCanonical(parsed);
    if (referralCleanup.didClean) {
      parsed = referralCleanup.canonical;
      if (parsed) {
        try {
          const payload = JSON.stringify(parsed);
          localStorage.setItem(STORAGE_KEY, payload);
          writeCookie(COOKIE_KEY, payload);
        } catch (e) {}
      }
    }

    return normalizeCanonical(parsed);
  }

  function persistCanonicalAttribution(canonical) {
    if (!shouldPersistAttribution()) return;
    try {
      const payload = JSON.stringify(canonical);
      localStorage.setItem(STORAGE_KEY, payload);
      writeCookie(COOKIE_KEY, payload);
    } catch (e) {}
  }

  function getLastTouch(canonical) {
    const current = canonical || window.__sternAttribution || {};
    const lastTouch = sanitizeTouch(current.last_touch);
    if (hasAnyValue(lastTouch)) return lastTouch;
    return sanitizeTouch(current.first_touch);
  }

  function getFirstTouch(canonical) {
    const current = canonical || window.__sternAttribution || {};
    return sanitizeTouch(current.first_touch);
  }

  function getMergedUtm(canonical) {
    const lastTouch = getLastTouch(canonical);
    const urlUtm = readQueryParams(UTM_KEYS);
    const merged = {};
    UTM_KEYS.forEach((key) => {
      const value = sanitizeString(urlUtm[key] || lastTouch[key]);
      if (value) merged[key] = value;
    });
    return merged;
  }

  function getClickIds(canonical) {
    const lastTouch = getLastTouch(canonical);
    const urlClickIds = readClickIdsFromUrlOrCookie();
    const merged = {};
    CLICK_ID_KEYS.forEach((key) => {
      const value = sanitizeString(urlClickIds[key] || lastTouch[key]);
      if (value) merged[key] = value;
    });
    return merged;
  }

  function getDealSource(utmMerged, canonical) {
    const can = canonical || getCanonicalAttribution();
    const origin = sanitizeString(can.source_origin, 40).toLowerCase();
    if (origin === 'wp') return 'WP';
    return 'Shopify';
  }

  function getQueryDecoratorParams(canonical) {
    const queryParams = Object.assign({}, getMergedUtm(canonical), getClickIds(canonical));
    Object.keys(queryParams).forEach((key) => {
      if (!sanitizeString(queryParams[key])) {
        delete queryParams[key];
      }
    });
    return queryParams;
  }

  function isCrossDomainTarget(urlObj) {
    if (!urlObj || !urlObj.hostname) return false;
    const destinationHost = sanitizeString(urlObj.hostname, 128).toLowerCase();
    const currentHost = sanitizeString(window.location.hostname, 128).toLowerCase();

    if (!destinationHost || destinationHost === currentHost) return false;
    if (destinationHost === 'staging-sternpinball.kinsta.cloud') return true;
    if (destinationHost === 'sternpinball.com') return true;
    return destinationHost.endsWith('.sternpinball.com');
  }

  function decorateCrossDomainLink(anchorEl) {
    try {
      if (!anchorEl || typeof anchorEl.getAttribute !== 'function') return;
      const href = anchorEl.getAttribute('href');
      if (!href) return;
      if (href.charAt(0) === '#') return;
      if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0 || href.indexOf('javascript:') === 0) {
        return;
      }

      const targetUrl = new URL(href, window.location.origin);
      if (!isCrossDomainTarget(targetUrl)) return;

      const params = getQueryDecoratorParams(window.__sternAttribution);
      Object.keys(params).forEach((key) => {
        if (!targetUrl.searchParams.get(key)) {
          targetUrl.searchParams.set(key, params[key]);
        }
      });

      anchorEl.setAttribute('href', targetUrl.toString());
    } catch (e) {}
  }

  function decorateExistingCrossDomainLinks() {
    const links = document.querySelectorAll('a[href]');
    Array.prototype.forEach.call(links, (linkEl) => {
      decorateCrossDomainLink(linkEl);
    });
  }

  function getTouchWithPrefix(prefix, touch) {
    const prefixed = {};
    const safePrefix = sanitizeString(prefix, 24);
    const safeTouch = sanitizeTouch(touch);
    Object.keys(safeTouch).forEach((key) => {
      prefixed[safePrefix + key] = safeTouch[key];
    });
    return prefixed;
  }

  const now = Date.now();
  const canonical = loadCanonicalAttribution();
  const incoming = buildIncomingTouch(now);

  canonical.source_origin = incoming.sourceOrigin || canonical.source_origin || 'shopify';

  if (incoming.isQualifying && !canonical.first_touch_at) {
    canonical.first_touch = sanitizeTouch(incoming.touch);
    canonical.first_touch_at = now;
  }

  if (
    incoming.isQualifying &&
    (!canonical.last_touch_at || shouldUpdateLastTouch(canonical.last_touch, incoming.touch))
  ) {
    canonical.last_touch = sanitizeTouch(incoming.touch);
    canonical.last_touch_at = now;
  }

  const normalizedCanonical = normalizeCanonical(canonical);
  persistCanonicalAttribution(normalizedCanonical);

  window.__sternAttribution = normalizedCanonical;
  window.__sternAttributionApi = {
    getCanonical: function () {
      return window.__sternAttribution || normalizeCanonical(null);
    },
    getFirstTouch: function () {
      return getFirstTouch(window.__sternAttribution);
    },
    getLastTouch: function () {
      return getLastTouch(window.__sternAttribution);
    },
    getMergedUtm: function () {
      return getMergedUtm(window.__sternAttribution);
    },
    getClickIds: function () {
      return getClickIds(window.__sternAttribution);
    },
    getDealSource: function (utmMerged) {
      return getDealSource(utmMerged, window.__sternAttribution);
    },
    getQueryDecoratorParams: function () {
      return getQueryDecoratorParams(window.__sternAttribution);
    },
    getFirstTouchWithPrefix: function (prefix) {
      return getTouchWithPrefix(prefix || 'first_', getFirstTouch(window.__sternAttribution));
    },
    getLastTouchWithPrefix: function (prefix) {
      return getTouchWithPrefix(prefix || 'last_', getLastTouch(window.__sternAttribution));
    }
  };

  // Backward-compatible helpers used in legacy code below.
  window.__sternGetUtmMerged = function () {
    return window.__sternAttributionApi.getMergedUtm();
  };
  window.__sternGetDealSource = function (utmMerged) {
    return window.__sternAttributionApi.getDealSource(utmMerged);
  };

  if (Array.isArray(window.dataLayer) && window.__sternAttributionDataLayerPushed !== true) {
    window.__sternAttributionDataLayerPushed = true;
    const mergedUtm = window.__sternAttributionApi.getMergedUtm();
    const clickIds = window.__sternAttributionApi.getClickIds();
    var lastTouch = window.__sternAttributionApi.getLastTouch ? window.__sternAttributionApi.getLastTouch() : {};
    window.dataLayer.push({
      event: 'stern_attribution_ready',
      stern_attribution: window.__sternAttribution,
      deal_source: window.__sternAttributionApi.getDealSource(mergedUtm),
      referrer_hostname: lastTouch.referrer_hostname || '',
      landing_page: lastTouch.landing_page || '',
      utm_source: mergedUtm.utm_source || '',
      utm_medium: mergedUtm.utm_medium || '',
      utm_campaign: mergedUtm.utm_campaign || '',
      utm_content: mergedUtm.utm_content || '',
      utm_term: mergedUtm.utm_term || '',
      gclid: clickIds.gclid || '',
      dclid: clickIds.dclid || '',
      gbraid: clickIds.gbraid || '',
      wbraid: clickIds.wbraid || '',
      msclkid: clickIds.msclkid || '',
      fbclid: clickIds.fbclid || '',
      ttclid: clickIds.ttclid || '',
      twclid: clickIds.twclid || '',
      li_fat_id: clickIds.li_fat_id || '',
      pin_clickid: clickIds.pin_clickid || '',
      rdt_cid: clickIds.rdt_cid || '',
      canonical_source_origin: (window.__sternAttributionApi.getCanonical().source_origin) || ''
    });
  }

  const crossDomainDecorateHandler = function (event) {
    const anchorEl = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (anchorEl) decorateCrossDomainLink(anchorEl);
  };

  document.addEventListener('pointerdown', crossDomainDecorateHandler, true);
  document.addEventListener('focusin', crossDomainDecorateHandler, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', decorateExistingCrossDomainLinks);
  } else {
    decorateExistingCrossDomainLinks();
  }
})();

// Vue mount fallback: if Vue fails to mount, remove v-cloak so UI is still visible.
(function releaseVueCloakFallback() {
    function clearCloakIfNeeded() {
        const app = document.getElementById("vue-app");
        if (!app || !app.hasAttribute("v-cloak")) return;
        if (!app.__vue__) {
            app.removeAttribute("v-cloak");
        }
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            setTimeout(clearCloakIfNeeded, 300);
        });
    } else {
        setTimeout(clearCloakIfNeeded, 300);
    }
})();

const typeValues = document.querySelectorAll(".type-product__value ");
if (typeValues.length) {
    typeValues.forEach((value) => {
        value.addEventListener("click", (e) => {
            const options = document.querySelectorAll(".blobselect-items div");
            const prices = document.querySelectorAll(".product-price");
            options.forEach((option) => {
                const optionText = option.textContent || "";
                const type = optionText.split(" ")[0].trim();
                const newPrice = optionText.split(" ").pop();
                const targetType = ((e.target && e.target.textContent) || "").split(" ")[0].trim();
                if (type === targetType) {
                    $(option).click();
                    prices.forEach((price) => {
                        price.textContent = newPrice;
                    });
                }
            });
            typeValues.forEach((item) => {
                item.classList.remove("active-value-product");
            });
            value.classList.add("active-value-product");
        });
    });
}

if (window.location.toString().includes("/products")) {
    var addActiveClass = true;
    typeValues.forEach(function (elem) {
        if (elem.classList.contains("active-value-product")) {
            addActiveClass = false;
        }
    });

    if (addActiveClass && !(typeof typeValues[0] === "undefined")) {
        typeValues[0].classList.add("active-value-product");
    }
}

// const typeProduct = document.querySelector('.type-product')
//   if (typeProduct) {
//         setTimeout(() => {
//             const blobselect = document.querySelector('#product-variants')
//            blobselect.style.display = 'none'

//           }, "2000")
//   }

function getTimeRemaining(endtime) {
    var t = Date.parse(endtime) - Date.parse(new Date());
    var seconds = Math.floor((t / 1000) % 60);
    var minutes = Math.floor((t / 1000 / 60) % 60);
    var hours = Math.floor((t / (1000 * 60 * 60)) % 24);
    var days = Math.floor(t / (1000 * 60 * 60 * 24));
    return {
        total: t,
        days: days,
        hours: hours,
        minutes: minutes,
        seconds: seconds,
    };
}

function initializeClock(id, endtime) {
    var clock = document.getElementById(id);
    if (!clock) return;
    var daysSpan = clock.querySelector(".days");
    var hoursSpan = clock.querySelector(".hours");
    var minutesSpan = clock.querySelector(".minutes");
    var secondsSpan = clock.querySelector(".seconds");
    if (!daysSpan || !hoursSpan || !minutesSpan || !secondsSpan) return;

    function updateClock() {
        var t = getTimeRemaining(endtime);

        daysSpan.innerHTML = t.days;
        hoursSpan.innerHTML = ("0" + t.hours).slice(-2);
        minutesSpan.innerHTML = ("0" + t.minutes).slice(-2);
        secondsSpan.innerHTML = ("0" + t.seconds).slice(-2);
        if (t.days <= 0) {
            const dayContainer = document.querySelector(".countdown-number-days");
            if (dayContainer) {
                dayContainer.style.display = "none";
            }
        }
        if (t.days == 1) {
            const dayText = document.querySelector(
                ".countdown-number-days .countdown-text"
            );
            if (dayText) {
                dayText.innerHTML = "Day";
            }
        }
        if (t.total <= 0) {
            clearInterval(timeinterval);
            const dropTimeContainer = document.querySelector(".header-with-timer");
            if (dropTimeContainer) {
                dropTimeContainer.style.display = "none";
            }
        }
    }

    updateClock();
    var timeinterval = setInterval(updateClock, 1000);
}
const dropTimeContainer = document.querySelector(".header-with-timer__time");
if (dropTimeContainer) {
    let dropTimer = dropTimeContainer.getAttribute("date-atr");
    if (!dropTimer) {
        dropTimer = "";
    }
    dropTimer = dropTimer.split("-");
    if (dropTimer.length >= 5) {
        const dropYear = dropTimer[0];
        const dropMonth = dropTimer[1];
        const dropDay = dropTimer[2];
        const dropHour = dropTimer[3];
        const dropMinutes = dropTimer[4];
        var deadline = `${dropMonth} ${dropDay} ${dropYear} ${dropHour}:${dropMinutes}:00 GMT+0300`;
        initializeClock("countdown", deadline);
    }
}

// --- STRAT. Integration with WordPress site and form processing. ---
const btnContactForAvailability = document.querySelector(
    ".btn-contact-for-availability"
);
const contactForm = document.querySelector(".modal-contact-for-availability");
const contactFormClose = document.querySelector(".contact-form-close");
const contactFormBackground = document.querySelector(
    ".contact-form-background"
);
const contactFormSumbit = document.querySelector(".contact-form-submit");
const body = document.querySelector("body");

const COUNTRY_PLACEHOLDER_VALUE = "__select_country__";
const countrySelect = document.getElementById('countrySelect');
const countries = window.countryRegionData;
// Add an empty default option
const defaultOption = document.createElement('option');
defaultOption.value = COUNTRY_PLACEHOLDER_VALUE;
defaultOption.textContent = "Select Country";
defaultOption.disabled = true;
defaultOption.selected = true;
if (countrySelect) {
    countrySelect.appendChild(defaultOption);
}

if (countrySelect && countries) {
    // Create an array for countries that aren't USA or Canada
    const otherCountries = countries.filter(
        country => country.countryName !== "United States" && country.countryName !== "Canada"
    );

    // Sort the other countries alphabetically by countryName
    otherCountries.sort((a, b) => a.countryName.localeCompare(b.countryName));

    // Add USA and Canada at the beginning
    const importantCountries = ["United States", "Canada"];
    importantCountries.forEach(countryName => {
        const option = document.createElement('option');
        option.value = countryName;
        option.textContent = countryName;
        countrySelect.appendChild(option);
    });

    // Add the sorted other countries
    otherCountries && otherCountries.forEach(country => {
        const option = document.createElement('option');
        option.value = country.countryName;
        option.textContent = country.countryName;
        countrySelect.appendChild(option);
    });
}

const CFA_SUBMISSION_LOCK_MS = 30 * 1000;
window.__sternCfaSubmissionIds = window.__sternCfaSubmissionIds || {};
window.__sternFreshsalesSubmissionIds = window.__sternFreshsalesSubmissionIds || {};

function cfaSanitizeString(value, maxLength) {
    const limit = typeof maxLength === "number" ? maxLength : 512;
    if (value == null) return "";
    return String(value).trim().slice(0, limit);
}

function isCountrySelectionInvalid(value) {
    const normalized = cfaSanitizeString(value, 120).toLowerCase();
    return (
        normalized.length === 0 ||
        normalized === COUNTRY_PLACEHOLDER_VALUE ||
        normalized === "select country"
    );
}

function ensureCfaRequiredFields(formElement) {
    if (!formElement || !formElement.querySelector) return;
    const selectors = ["#first-name", "#last-name", "#email", "#zip-code", "#countrySelect"];
    selectors.forEach((selector) => {
        const field = formElement.querySelector(selector);
        if (!field || !field.setAttribute) return;
        field.setAttribute("required", "required");
        field.setAttribute("aria-required", "true");
    });
}

function cfaNormalizePhoneDigits(value, maxLength) {
    const limit = typeof maxLength === "number" ? maxLength : 15;
    if (value == null) return "";
    return String(value).replace(/\D+/g, "").slice(0, limit);
}

function cfaSyncPhoneInputValue(phoneField) {
    if (!phoneField || typeof phoneField.value === "undefined") return "";
    const normalized = cfaNormalizePhoneDigits(phoneField.value, 15);
    if (phoneField.value !== normalized) {
        phoneField.value = normalized;
    }
    return normalized;
}

function cfaReadCookieByName(name) {
    try {
        const pattern = "(?:^|;\\s*)" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)";
        const match = document.cookie.match(new RegExp(pattern));
        return match ? decodeURIComponent(match[1]) : "";
    } catch (error) {
        return "";
    }
}

function getAttributionApi() {
    return window.__sternAttributionApi && typeof window.__sternAttributionApi === "object"
        ? window.__sternAttributionApi
        : null;
}

function getCanonicalAttribution() {
    const api = getAttributionApi();
    if (api && typeof api.getCanonical === "function") {
        return api.getCanonical() || {};
    }
    return window.__sternAttribution || {};
}

function getMergedUtm() {
    const api = getAttributionApi();
    if (api && typeof api.getMergedUtm === "function") {
        return api.getMergedUtm() || {};
    }
    const params = new URLSearchParams(window.location.search);
    const fallback = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
        const value = cfaSanitizeString(params.get(key));
        if (value) fallback[key] = value;
    });
    return fallback;
}

function getClickIds() {
    const api = getAttributionApi();
    if (api && typeof api.getClickIds === "function") {
        return api.getClickIds() || {};
    }
    const params = new URLSearchParams(window.location.search);
    const fallback = {};
    [
        "gclid",
        "dclid",
        "gbraid",
        "wbraid",
        "msclkid",
        "fbclid",
        "ttclid",
        "twclid",
        "li_fat_id",
        "pin_clickid",
        "rdt_cid"
    ].forEach((key) => {
        const value = cfaSanitizeString(params.get(key));
        if (value) fallback[key] = value;
    });

    if (!fallback.rdt_cid) {
        const rdtCidCookie = cfaSanitizeString(cfaReadCookieByName("rdt_cid"), 128);
        if (rdtCidCookie) fallback.rdt_cid = rdtCidCookie;
    }
    return fallback;
}

function getTouchWithPrefix(prefix, touchData) {
    const touch = touchData && typeof touchData === "object" ? touchData : {};
    const result = {};
    const safePrefix = cfaSanitizeString(prefix, 24);
    Object.keys(touch).forEach((key) => {
        if (key === "landing_ts") {
            const parsedTs = Number(touch[key]);
            result[safePrefix + key] = Number.isFinite(parsedTs) && parsedTs > 0 ? String(Math.floor(parsedTs)) : "";
            return;
        }
        result[safePrefix + key] = cfaSanitizeString(touch[key], 768);
    });
    return result;
}

function getFormValue(formElement, selectors, maxLen) {
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];
    const limit = typeof maxLen === "number" ? maxLen : 1024;
    for (let i = 0; i < selectorList.length; i++) {
        const selector = selectorList[i];
        if (!selector) continue;
        const field = (formElement && formElement.querySelector(selector)) || document.querySelector(selector);
        if (!field || typeof field.value === "undefined") continue;
        const value = cfaSanitizeString(field.value, limit);
        if (value) return value;
    }
    return "";
}

function parseWpApiResponse(response) {
    return response.text().then((bodyText) => {
        let parsed = null;
        try {
            parsed = bodyText ? JSON.parse(bodyText) : null;
        } catch (e) {
            parsed = null;
        }

        if (!response.ok) {
            const messageFromBody = parsed && typeof parsed.message === "string" ? parsed.message : "";
            const error = new Error(messageFromBody || ("Request failed with status " + response.status));
            error.status = response.status;
            error.statusText = response.statusText || "";
            error.code = parsed && parsed.code ? String(parsed.code) : "";
            error.body = bodyText || "";
            throw error;
        }
        if (parsed && parsed.ok === false) {
            const error = new Error(parsed.message || "Submission failed");
            error.status = response.status || 500;
            error.code = parsed.code ? String(parsed.code) : "submission_failed";
            error.body = bodyText || "";
            throw error;
        }
        return { text: bodyText, parsed: parsed };
    });
}

function submitContactFallback(formElement, submitButton, context) {
    if (window.__sternEnableShopifyContactFallback !== true || !formElement) return false;
    const countryField = formElement.querySelector ? formElement.querySelector("#countrySelect") : null;
    const countryValue = cfaSanitizeString(countryField ? countryField.value : "", 120);
    if (isCountrySelectionInvalid(countryValue)) {
        if (window.alertNotification && typeof window.alertNotification.error === "function") {
            window.alertNotification.error("Please select a country.");
        }
        releaseSubmissionLock(formElement, submitButton, false);
        return false;
    }
    const details = context || {};
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "contact_for_availability_fallback_submit",
            submission_id: cfaSanitizeString(details.submissionId, 120),
            fallback_reason: cfaSanitizeString(details.reason, 120),
            fallback_status: cfaSanitizeString(details.status, 12),
            fallback_code: cfaSanitizeString(details.code, 120)
        });
    } catch (e) {}

    releaseSubmissionLock(formElement, submitButton, true);
    try {
        formElement.dataset.sternCfaAllowNative = "true";
    } catch (e) {}

    // Use a native submit path so Shopify CAPTCHA token generation can run.
    if (submitButton && typeof submitButton.click === "function") {
        submitButton.click();
        return true;
    }
    if (typeof formElement.requestSubmit === "function") {
        formElement.requestSubmit();
        return true;
    }
    return true;
}

function readVariantFromProductUrl(productUrl) {
    try {
        if (!productUrl) return "";
        const parsed = new URL(productUrl, window.location.origin);
        return cfaSanitizeString(parsed.searchParams.get("variant"), 120);
    } catch (e) {
        return "";
    }
}

function buildProductUrlFromHandle(productHandle) {
    const handle = cfaSanitizeString(productHandle, 220);
    if (!handle) return "";
    return cfaSanitizeString(
        "https://shop.sternpinball.com/collections/games/products/" + encodeURIComponent(handle),
        1200
    );
}

function buildProductTitleFromHandle(productHandle) {
    const handle = cfaSanitizeString(productHandle, 220);
    if (!handle) return "";
    const text = handle
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!text) return "";
    return cfaSanitizeString(text.replace(/\b\w/g, function (match) { return match.toUpperCase(); }), 240);
}

function buildCfaSubmissionId() {
    return "cfa_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
}

function buildSubmissionFingerprint(formElement) {
    if (!formElement) return "";
    const emailField = formElement.querySelector("#email");
    const zipCodeField = formElement.querySelector("#zip-code");
    const productField = formElement.querySelector("#contact-form-product-title");
    const variantField = formElement.querySelector("#variant");
    const mergedUtm = getMergedUtm();
    return [
        cfaSanitizeString(emailField && emailField.value, 120).toLowerCase(),
        cfaSanitizeString(zipCodeField && zipCodeField.value, 40).toLowerCase(),
        cfaSanitizeString(productField && productField.value, 180).toLowerCase(),
        cfaSanitizeString(variantField && variantField.value, 120).toLowerCase(),
        cfaSanitizeString(mergedUtm.utm_source, 120).toLowerCase(),
        cfaSanitizeString(mergedUtm.utm_medium, 120).toLowerCase(),
        cfaSanitizeString(mergedUtm.utm_campaign, 120).toLowerCase()
    ].join("|");
}

function setSubmitButtonState(buttonElement, isDisabled) {
    if (!buttonElement) return;
    buttonElement.disabled = isDisabled === true;
    if (buttonElement.tagName === "INPUT") {
        buttonElement.setAttribute("aria-disabled", isDisabled ? "true" : "false");
    }
}

function acquireSubmissionLock(formElement, submitButton) {
    if (!formElement) return { locked: false, reason: "missing-form" };
    const now = Date.now();
    const fingerprint = buildSubmissionFingerprint(formElement);
    const lastFingerprint = cfaSanitizeString(formElement.dataset.sternCfaFingerprint, 512);
    const lastSubmissionTs = Number(formElement.dataset.sternCfaLastSubmittedAt || 0);

    if (formElement.dataset.sternCfaSubmitting === "true") {
        return { locked: false, reason: "in-flight" };
    }
    if (
        fingerprint &&
        lastFingerprint &&
        fingerprint === lastFingerprint &&
        Number.isFinite(lastSubmissionTs) &&
        now - lastSubmissionTs < CFA_SUBMISSION_LOCK_MS
    ) {
        return { locked: false, reason: "duplicate" };
    }

    formElement.dataset.sternCfaSubmitting = "true";
    formElement.dataset.sternCfaFingerprint = fingerprint;
    formElement.dataset.sternCfaLastSubmittedAt = String(now);
    setSubmitButtonState(submitButton, true);
    return { locked: true, reason: "ok", fingerprint: fingerprint };
}

function releaseSubmissionLock(formElement, submitButton, keepRecentLock) {
    if (!formElement) return;
    formElement.dataset.sternCfaSubmitting = "false";
    if (keepRecentLock !== true) {
        formElement.dataset.sternCfaFingerprint = "";
        formElement.dataset.sternCfaLastSubmittedAt = "0";
    }
    setSubmitButtonState(submitButton, false);
}

function upsertHiddenField(formElement, fieldName, fieldValue) {
    if (!formElement || !fieldName) return;
    let field = formElement.querySelector('input[name="' + fieldName + '"]');
    if (!field) {
        field = document.createElement("input");
        field.type = "hidden";
        field.name = fieldName;
        formElement.appendChild(field);
    }
    field.value = fieldValue == null ? "" : String(fieldValue);
}

function applyContactFormMetaFields(formElement, meta) {
    if (!formElement) return;
    const safeMeta = meta || {};
    Object.keys(safeMeta).forEach((key) => {
        upsertHiddenField(formElement, "contact[" + key + "]", safeMeta[key] || "");
    });
}

function buildContactSubmitMeta(context) {
    const safeContext = context || {};
    const canonical = safeContext.canonicalAttribution || getCanonicalAttribution();
    const mergedUtm = safeContext.utmMerged || getMergedUtm();
    const clickIds = safeContext.clickIds || getClickIds();
    const firstTouch = canonical.first_touch || {};
    const lastTouch = canonical.last_touch || canonical.first_touch || {};
    const queryParams = new URLSearchParams(window.location.search);
    const utmSource = cfaSanitizeString(mergedUtm.utm_source || queryParams.get("utm_source") || "", 120);
    const utmMedium = cfaSanitizeString(mergedUtm.utm_medium || queryParams.get("utm_medium") || "", 120);
    const utmCampaign = cfaSanitizeString(mergedUtm.utm_campaign || queryParams.get("utm_campaign") || "", 160);
    const dealSource = cfaSanitizeString(
        safeContext.dealSource ||
        (window.__sternGetDealSource ? window.__sternGetDealSource(null, canonical) : "Shopify"),
        80
    );
    const inferredShopType = String(dealSource).toUpperCase() === "WP" ? "WP" : "Shopify";
    const submissionId = cfaSanitizeString(safeContext.submissionId || buildCfaSubmissionId(), 120);
    const isCesCampaign =
        cfaSanitizeString(queryParams.get("utm_source"), 64).toLowerCase() === "ces" &&
        cfaSanitizeString(queryParams.get("utm_medium"), 64).toLowerCase() === "event" &&
        cfaSanitizeString(queryParams.get("utm_campaign"), 64).toLowerCase() === "ces2026";
    const meta = {
        submission_id: submissionId,
        deal_source: dealSource,
        inquiry_source: cfaSanitizeString(safeContext.inquirySource || inferredShopType, 80),
        shop_type: cfaSanitizeString(safeContext.shopType || inferredShopType, 80),
        referrer_hostname: cfaSanitizeString(lastTouch.referrer_hostname, 128),
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: cfaSanitizeString(mergedUtm.utm_content || queryParams.get("utm_content"), 200),
        utm_term: cfaSanitizeString(mergedUtm.utm_term || queryParams.get("utm_term"), 200),
        ces_campaign: isCesCampaign ? "ces2026" : "",
        canonical_schema_version: String(Number(canonical.schema_version || 0) || 0),
        canonical_source_origin: cfaSanitizeString(canonical.source_origin, 40),
        first_touch_at: String(Number(canonical.first_touch_at || 0) || 0),
        last_touch_at: String(Number(canonical.last_touch_at || 0) || 0),
        session_start_at: String(Number(canonical.session_start_at || 0) || 0),
        session_last_at: String(Number(canonical.session_last_at || 0) || 0)
    };

    [
        "gclid",
        "dclid",
        "gbraid",
        "wbraid",
        "msclkid",
        "fbclid",
        "ttclid",
        "twclid",
        "li_fat_id",
        "pin_clickid",
        "rdt_cid"
    ].forEach((key) => {
        meta[key] = cfaSanitizeString(clickIds[key] || lastTouch[key], 256);
    });

    Object.assign(meta, getTouchWithPrefix("first_", firstTouch));
    Object.assign(meta, getTouchWithPrefix("last_", lastTouch));

    try {
        meta.attribution_json = JSON.stringify({
            schema_version: Number(canonical.schema_version || 0) || 0,
            source_origin: cfaSanitizeString(canonical.source_origin, 40),
            first_touch_at: Number(canonical.first_touch_at || 0) || 0,
            last_touch_at: Number(canonical.last_touch_at || 0) || 0,
            first_touch: firstTouch,
            last_touch: lastTouch,
            click_ids: clickIds
        });
    } catch (error) {
        meta.attribution_json = "";
    }

    return meta;
}

$("#contact_form .contact-form-submit input").click(function (e) {
    const submitButton = e.currentTarget;
    const form = document.getElementById("contact_form");
    ensureCfaRequiredFields(form);
    if (form && form.dataset && form.dataset.sternCfaAllowNative === "true") {
        form.dataset.sternCfaAllowNative = "false";
        return;
    }
    e.preventDefault();
    const lockState = acquireSubmissionLock(form, submitButton);
    if (!lockState.locked) {
        if (lockState.reason === "duplicate" || lockState.reason === "in-flight") {
            alertNotification.error("Your request is already being submitted.");
        }
        return;
    }

    const labelFirstName = document.querySelector('.contact-form .first-name-contact-form label');
    const labelLastName = document.querySelector('.contact-form .last-name-contact-form label');
    const labelEmail = document.querySelector('.contact-form .email-contact-form label');
    const labelPostalCode = document.querySelector('.contact-form .zip-code label');
    let labelCountry = document.querySelector('.contact-form .select-contact-form label');
    const selectEdition = document.querySelector(".select_editions .v_bg\\:red-400");
    const variantInput = document.querySelector('#contact_form #variant');
    if (selectEdition) {
        let selectEditionText = selectEdition.textContent.trim();

        if (selectEditionText === 'limited edition') {
            selectEditionText = 'LE';
        }

        if (variantInput) {
            variantInput.value = selectEditionText;
        }
    } else if (variantInput && !variantInput.value) {
        variantInput.value = " ";
    }
    const firstName = cfaSanitizeString($('#contact_form #first-name').val(), 120);
    const lastName = cfaSanitizeString($('#contact_form #last-name').val(), 120);
    const emailInput = cfaSanitizeString($('#contact_form #email').val(), 180);
    const postalCode = cfaSanitizeString($('#contact_form #zip-code').val(), 40);
    const country = cfaSanitizeString($('#contact_form #countrySelect').val(), 120);
    const phoneField = form ? form.querySelector('#phone') : document.querySelector('#contact_form #phone');
    const phoneDigits = cfaSyncPhoneInputValue(phoneField);

    // Array to store error messages
    let errorMessages = [];
    validateField(firstName, labelFirstName, 'First name is required.', errorMessages);
    validateCountryField(country, labelCountry, errorMessages);
    validateField(postalCode, labelPostalCode, 'Postal code is required.', errorMessages);
    validateField(emailInput, labelEmail, 'Email is required and must be valid.', errorMessages);

    // Display all error messages at once
    if (errorMessages.length > 0) {
        alertNotification.error(errorMessages.join('\n'));
        releaseSubmissionLock(form, submitButton, false);
        return;
    }

    if (isCountrySelectionInvalid(country)) {
        alertNotification.error('Please select a country.');
        labelCountry = document.querySelector('.contact-form .select-contact-form label');
        if (labelCountry) labelCountry.style.color = "#D0021B";
        releaseSubmissionLock(form, submitButton, false);
        return;
    }

    const canonicalAttribution = getCanonicalAttribution();
    const utmMerged = getMergedUtm();
    const clickIds = getClickIds();
    const dealSource = (window.__sternGetDealSource ? window.__sternGetDealSource(null, canonicalAttribution) : 'Shopify');
    const inferredShopType = (String(dealSource).toUpperCase() === "WP") ? "WP" : "Shopify";
    const selectedShopType = $("#contact_form #shopType").val() || inferredShopType;
    const submissionId = buildCfaSubmissionId();
    const contactSubmitMeta = buildContactSubmitMeta({
        submissionId: submissionId,
        canonicalAttribution: canonicalAttribution,
        utmMerged: utmMerged,
        clickIds: clickIds,
        dealSource: dealSource,
        inquirySource: selectedShopType,
        shopType: selectedShopType
    });

    applyContactFormMetaFields(form, contactSubmitMeta);
    const pageParams = new URLSearchParams(window.location.search);
    const productHandleFromUrl = cfaSanitizeString(pageParams.get("product"), 220);
    const productUrlValue = getFormValue(form, ["#contact-form-product-url", "#product-url"], 1200) ||
        buildProductUrlFromHandle(productHandleFromUrl);
    const productPriceValue = getFormValue(form, ["#contact-form-product-pr", "#contact-form-product-price"], 120);
    const productTitleValue = getFormValue(form, ["#contact-form-product-title"], 240) ||
        cfaSanitizeString(pageParams.get("title"), 240) ||
        cfaSanitizeString(pageParams.get("product_title"), 240) ||
        buildProductTitleFromHandle(productHandleFromUrl);
    const variantValue = getFormValue(form, ["#variant"], 160) ||
        cfaSanitizeString(pageParams.get("variant"), 160) ||
        cfaSanitizeString(pageParams.get("product_variant"), 160) ||
        readVariantFromProductUrl(productUrlValue);

    const dataArr = {
        firstName: $("#contact_form #first-name").val(),
        lastName: $("#contact_form #last-name").val(),
        phone: phoneDigits,
        email: $("#contact_form #email").val(),
        zipCode: $("#contact_form #zip-code").val(),
        zipcode: $("#contact_form #zip-code").val(),
        // city: $('#contact_form #city').val(),
        source: $("#contact_form #select").val(),
        // selectTwo: $('#contact_form #select-two').val(),
        specification: $("#contact_form #specification").val(),
        location: $('#location').val(),
        locationAnswer: $('#locationAnswer').val(),
        message: $("#contact_form #message").val(),
        productUrl: productUrlValue,
        productTitle: productTitleValue,
        productPrice: productPriceValue,
        PFamily: $("#contact_form #ip_family").val(),
        Version: $("#contact_form #version").val(),
        Variant: variantValue,
        shopType: selectedShopType,
        ProductCategory:"Pinball Machine",
        InquirySource: selectedShopType,
        country: country
    };
    Object.assign(dataArr, contactSubmitMeta);

    const cleanedDataArr = Object.fromEntries(
        Object.entries(dataArr).filter(([_, v]) => v != null && v !== "")
    );
    cleanedDataArr.firstName = cfaSanitizeString(cleanedDataArr.firstName || firstName, 120);
    cleanedDataArr.lastName = cfaSanitizeString(cleanedDataArr.lastName || lastName, 120);
    cleanedDataArr.email = cfaSanitizeString(cleanedDataArr.email || emailInput, 180);

    var fsPayloadBase = {
        submission_id: submissionId,
        firstName: firstName,
        lastName: lastName,
        phone: phoneDigits,
        email: emailInput,
        zipCode: postalCode,
        zipcode: postalCode,
        source: $("#contact_form #select").val(),
        specification: $("#contact_form #specification").val(),
        message: $("#contact_form #message").val(),
        productUrl: productUrlValue,
        productTitle: productTitleValue,
        productPrice: productPriceValue,
        InquirySource: selectedShopType,
        location: $('#location').val(),
        locationAnswer: $('#locationAnswer').val(),
        IPFamily: $("#contact_form #ip_family").val(),
        Version: $("#contact_form #version").val(),
        Variant: variantValue,
        ProductCategory: "Pinball Machine",
        country: country
    };
    Object.assign(fsPayloadBase, contactSubmitMeta);
    fsPayloadBase["product-title"] = productTitleValue;
    fsPayloadBase.product_title = productTitleValue;
    fsPayloadBase.variant = variantValue;
    fsPayloadBase["product-variant"] = variantValue;
    fsPayloadBase.product_variant = variantValue;
    fsPayloadBase["shop-type"] = selectedShopType;
    fsPayloadBase.shop_type = selectedShopType;
    fsPayloadBase.attribution = contactSubmitMeta;

    function trackFreshsalesForSubmission(extraMeta) {
        if (window.__sternFreshsalesSubmissionIds[submissionId]) {
            return false;
        }

        var fsPayload = Object.assign({}, fsPayloadBase, extraMeta || {});
        var identifyPayload = {
            "First name": cfaSanitizeString(fsPayload.firstName, 120),
            "Email": cfaSanitizeString(fsPayload.email, 180),
            "Country": cfaSanitizeString(fsPayload.country, 120),
            "zipcode": cfaSanitizeString(fsPayload.zipcode || fsPayload.zipCode, 40),
            "Product Title": cfaSanitizeString(fsPayload["product-title"] || fsPayload.productTitle, 240),
            "Variant": cfaSanitizeString(fsPayload.variant || fsPayload.Variant, 160),
            "Product URL": cfaSanitizeString(fsPayload.productUrl, 1200),
            "Inquiry Source": cfaSanitizeString(fsPayload.InquirySource || fsPayload.shopType, 120),
            "product-title": cfaSanitizeString(fsPayload["product-title"] || fsPayload.productTitle, 240),
            "product_variant": cfaSanitizeString(fsPayload.product_variant || fsPayload.variant || fsPayload.Variant, 160),
            "referrer_hostname": cfaSanitizeString(fsPayload.referrer_hostname, 128),
            "deal_source": cfaSanitizeString(fsPayload.deal_source, 80),
            "IP Family": cfaSanitizeString(fsPayload.IPFamily, 120)
        };
        var sanitizedLastName = cfaSanitizeString(fsPayload.lastName, 120);
        if (sanitizedLastName) {
            identifyPayload["Last name"] = sanitizedLastName;
        }
        if (fsPayload.phone) {
            identifyPayload["Phone"] = cfaNormalizePhoneDigits(fsPayload.phone, 15);
        }
        var identifyPayloadSafe = {
            "First name": cfaSanitizeString(fsPayload.firstName, 120),
            "Email": cfaSanitizeString(fsPayload.email, 180),
            "Country": cfaSanitizeString(fsPayload.country, 120),
            "zipcode": cfaSanitizeString(fsPayload.zipcode || fsPayload.zipCode, 40)
        };
        if (sanitizedLastName) {
            identifyPayloadSafe["Last name"] = sanitizedLastName;
        }
        var canIdentify = window.fwcrm && typeof window.fwcrm.identify === 'function';
        var canAssociate = window.FM && typeof window.FM.associateVisitor === 'function';
        var canTrackCustomEvent = window.FM && typeof window.FM.trackCustomEvent === 'function';
        var canTrackMasEvent = window.FM && typeof window.FM.trackMasEvent === 'function';
        var didIdentify = false;
        var didIdentifyEnriched = false;
        var identifyFallbackUsed = false;
        var didAssociate = false;
        var didTrackCustomEvent = false;
        var didTrackMasEvent = false;
        var freshsalesErrors = [];

        if (canIdentify && fsPayload.email) {
            try {
                // Send minimal identity first to avoid optional field validation blocking contact creation.
                window.fwcrm.identify(fsPayload.email, identifyPayloadSafe);
                didIdentify = true;
            } catch (identifySafeError) {
                freshsalesErrors.push("identify_safe:" + (identifySafeError && identifySafeError.message ? String(identifySafeError.message) : "unknown"));
            }

            if (didIdentify) {
                try {
                    window.fwcrm.identify(fsPayload.email, identifyPayload);
                    didIdentifyEnriched = true;
                } catch (identifyFullError) {
                    freshsalesErrors.push("identify_full:" + (identifyFullError && identifyFullError.message ? String(identifyFullError.message) : "unknown"));
                }
            } else {
                // Last resort: try enriched identify if safe identify failed.
                try {
                    window.fwcrm.identify(fsPayload.email, identifyPayload);
                    didIdentify = true;
                    didIdentifyEnriched = true;
                    identifyFallbackUsed = true;
                } catch (identifyFallbackError) {
                    freshsalesErrors.push("identify_full:" + (identifyFallbackError && identifyFallbackError.message ? String(identifyFallbackError.message) : "unknown"));
                }
            }
        }

        if (canAssociate && fsPayload.email) {
            try {
                window.FM.associateVisitor(fsPayload.email);
                didAssociate = true;
            } catch (associateError) {
                freshsalesErrors.push("associate:" + (associateError && associateError.message ? String(associateError.message) : "unknown"));
            }
        }

        if (canTrackCustomEvent) {
            try {
                window.FM.trackCustomEvent("Contact For Availability Form Submitted", fsPayload);
                didTrackCustomEvent = true;
            } catch (customEventError) {
                freshsalesErrors.push("custom_event:" + (customEventError && customEventError.message ? String(customEventError.message) : "unknown"));
            }
        }

        if (canTrackMasEvent) {
            try {
                window.FM.trackMasEvent("web_form", true, {
                    digest: "contact_for_availability",
                    data: fsPayload
                });
                didTrackMasEvent = true;
            } catch (masEventError) {
                freshsalesErrors.push("mas_event:" + (masEventError && masEventError.message ? String(masEventError.message) : "unknown"));
            }
        }

        if (didTrackCustomEvent || didTrackMasEvent || didIdentify || didAssociate) {
            window.__sternFreshsalesSubmissionIds[submissionId] = true;
        }

        if (freshsalesErrors.length > 0) {
            console.error("Freshsales tracking partially failed:", freshsalesErrors);
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "freshsales_track_failed",
                submission_id: submissionId,
                reason: "partial_failure",
                message: freshsalesErrors.join(" | ").slice(0, 500),
                can_identify: canIdentify,
                can_associate: canAssociate,
                can_track_custom_event: canTrackCustomEvent,
                can_track_mas_event: canTrackMasEvent
            });
        }

        if (didTrackCustomEvent || didTrackMasEvent || didIdentify || didAssociate) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'freshsales_track_attempted',
                submission_id: submissionId,
                identify_sent: didIdentify,
                identify_enriched_sent: didIdentifyEnriched,
                identify_fallback_used: identifyFallbackUsed,
                associated_visitor: didAssociate,
                custom_event_sent: didTrackCustomEvent,
                web_form_event_sent: didTrackMasEvent,
                event_name: 'Contact For Availability Form Submitted'
            });
            return true;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'freshsales_track_skipped',
            reason: 'fm_not_available',
            can_identify: canIdentify,
            can_associate: canAssociate,
            can_track_custom_event: canTrackCustomEvent,
            can_track_mas_event: canTrackMasEvent,
            submission_id: submissionId
        });
        return false;
    }

    const requestData = JSON.stringify(cleanedDataArr);
    const requestHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };

    fetch('https://sternpinball.com/wp-json/shop/v1/sternpinball', {
        method: 'PUT',
        headers: requestHeaders,
        body: requestData,
        cache: 'no-store',
        mode: 'cors'
    })
        .then((response) => parseWpApiResponse(response))
        .then((responsePayload) => {
            window.__sternCfaLastResponse = responsePayload && responsePayload.parsed ? responsePayload.parsed : null;
            if (!window.__sternCfaSubmissionIds[submissionId]) {
                window.__sternCfaSubmissionIds[submissionId] = true;
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'contact_for_availability',
                    submission_id: submissionId,
                    form_source: 'product_modal',
                    method: 'form_submission',
                    deal_source: contactSubmitMeta.deal_source || '',
                    canonical_source_origin: contactSubmitMeta.canonical_source_origin || '',
                    referrer_hostname: contactSubmitMeta.referrer_hostname || '',
                    utm_source: contactSubmitMeta.utm_source || '',
                    utm_medium: contactSubmitMeta.utm_medium || '',
                    utm_campaign: contactSubmitMeta.utm_campaign || '',
                    utm_content: contactSubmitMeta.utm_content || '',
                    utm_term: contactSubmitMeta.utm_term || '',
                    gclid: contactSubmitMeta.gclid || '',
                    gbraid: contactSubmitMeta.gbraid || '',
                    wbraid: contactSubmitMeta.wbraid || '',
                    msclkid: contactSubmitMeta.msclkid || '',
                    fbclid: contactSubmitMeta.fbclid || '',
                    ttclid: contactSubmitMeta.ttclid || '',
                    twclid: contactSubmitMeta.twclid || '',
                    li_fat_id: contactSubmitMeta.li_fat_id || '',
                    pin_clickid: contactSubmitMeta.pin_clickid || '',
                    rdt_cid: contactSubmitMeta.rdt_cid || '',
                    first_touch_at: contactSubmitMeta.first_touch_at || '',
                    last_touch_at: contactSubmitMeta.last_touch_at || '',
                    page_path: window.location.pathname,
                    ces_campaign: contactSubmitMeta.ces_campaign || ''
                });
            }

            trackFreshsalesForSubmission({
                endpoint_status: 'success'
            });

            if (contactFormBackground) {
                contactFormBackground.classList.remove('active-contact-form-background');
            }
            if (body) body.classList.remove('active-contact-form-body');
            if (contactForm) contactForm.classList.remove('active-contact-form');

            alertNotification.success('Your request for availability has been sent.');
            setTimeout(function () {
                $('#contact_form input[type="text"], #contact_form input[type="email"], #contact_form input[type="number"], #contact_form input[type="tel"], #contact_form textarea, #contact_form select').val('');
            }, 0);
            releaseSubmissionLock(form, submitButton, true);
        })
        .catch((error) => {
            const status = Number(error && error.status) || 0;
            const errorCode = error && error.code ? String(error.code) : "";
            window.__sternCfaLastError = {
                status: status,
                code: errorCode,
                message: error && error.message ? String(error.message) : "",
                ts: Date.now()
            };

            trackFreshsalesForSubmission({
                endpoint_status: 'error',
                endpoint_error_status: status ? String(status) : "network",
                endpoint_error_code: errorCode || ""
            });

            if (submitContactFallback(form, submitButton, {
                submissionId: submissionId,
                reason: status === 401 || status === 403 ? "wp_auth_required" : "wp_endpoint_failed",
                status: status ? String(status) : "network",
                code: errorCode
            })) {
                return;
            }

            console.error('There was a problem with the fetch operation:', error);
            if (status === 400 && errorCode === 'stern_invalid_payload') {
                alertNotification.error('Please complete first name and a valid email before submitting.');
            } else {
                alertNotification.error('There was a problem sending your request. Please try again.');
            }
            releaseSubmissionLock(form, submitButton, false);
        });
});

$(document).on("submit", "#contact_form", function (e) {
    const form = e.currentTarget;
    ensureCfaRequiredFields(form);
    if (form && form.dataset && form.dataset.sternCfaAllowNative === "true") {
        form.dataset.sternCfaAllowNative = "false";
        return;
    }
    const submitButton = form && form.querySelector
        ? form.querySelector(".contact-form-submit input[type='submit']")
        : null;
    const countryField = form && form.querySelector ? form.querySelector("#countrySelect") : null;
    const countryValue = cfaSanitizeString(countryField ? countryField.value : "", 120);
    if (isCountrySelectionInvalid(countryValue)) {
        e.preventDefault();
        if (typeof e.stopImmediatePropagation === "function") {
            e.stopImmediatePropagation();
        }
        var labelCountry = document.querySelector('.contact-form .select-contact-form label');
        if (labelCountry) {
            labelCountry.style.color = "#D0021B";
        }
        alertNotification.error('Please select a country.');
        releaseSubmissionLock(form, submitButton, false);
        return;
    }
    e.preventDefault();
    if (submitButton && typeof submitButton.click === "function") {
        submitButton.click();
    }
});

if (!window.__sternCfaPhoneInputBound) {
    window.__sternCfaPhoneInputBound = true;
    $(document).on("input", "#contact_form #phone", function () {
        cfaSyncPhoneInputValue(this);
    });
    $(document).on("paste", "#contact_form #phone", function () {
        const field = this;
        window.setTimeout(function () {
            cfaSyncPhoneInputValue(field);
        }, 0);
    });
}

$("#select").change(function () {
    var specificationField = $("#contact_form #specification");

    if ($(this).val() === "Other") {
        specificationField.attr("placeholder", "N/A");
    } else {
        specificationField.attr("placeholder", "which one");
    }

    if (
        $(this).val() === "Event" ||
        $(this).val() === "Location" ||
        $(this).val() === "Other"
    ) {
        specificationField.show();
    } else {
        specificationField.hide();
    }
});

var locationAnswer = $('#contact_form #locationAnswer');
locationAnswer.hide();
$('#location').change(function() {
    if ($(this).val() === 'Other') {
        locationAnswer.attr('placeholder', 'N/A');
        locationAnswer.show();
    } else {
        locationAnswer.hide();
    }
});

if (btnContactForAvailability) {
    btnContactForAvailability.addEventListener("click", () => {
        if (contactForm) {
            contactForm.classList.add("active-contact-form");
            if (contactFormBackground) {
                contactFormBackground.classList.add("active-contact-form-background");
                body.classList.add("active-contact-form-body");
            }
        }
    });
}

if (contactFormClose) {
    contactFormClose.addEventListener("click", () => {
        if (contactForm) {
            contactForm.classList.remove("active-contact-form");
        }
        if (contactFormBackground) {
            contactFormBackground.classList.remove("active-contact-form-background");
            if (body) {
                body.classList.remove("active-contact-form-body");
            }
        }
    });
}
// if (contactFormSumbit) {
//    contactFormSumbit.addEventListener('click', () => {
//             contactForm.classList.remove('active-contact-form')
//           if (contactFormBackground) {
//                   contactFormBackground.classList.remove('active-contact-form-background')
//                   body.classList.remove('active-contact-form-body')

//              setTimeout(() => {
//            alert('Your letter has been sent');
//           }, "200")
//           }
//       })
// }
function validateField(inputValue, label, errorMessage, errorMessages) {
    const normalized = cfaSanitizeString(inputValue, 320);
    const isEmailRule = errorMessage.includes('valid');
    const isEmpty = normalized.length === 0;
    const isInvalidEmail = isEmailRule && !/^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/.test(normalized);
    if (isEmpty || isInvalidEmail) {
        if (label !== null && label !== undefined) {
            label.style.color = "#D0021B";
        }
        errorMessages.push(errorMessage);
    } else {
        if (label !== null && label !== undefined) {
            label.style.color = "#000";
        }
    }
}

function validateCountryField(countryValue, label, errorMessages) {
    if (isCountrySelectionInvalid(countryValue)) {
        if (label !== null && label !== undefined) {
            label.style.color = "#D0021B";
        }
        errorMessages.push('Country is required.');
    } else if (label !== null && label !== undefined) {
        label.style.color = "#000";
    }
}
// --- END. Integration with WordPress site and form processing. ---

setTimeout(() => {
    const affirmContainer = document.querySelector(".affirm-as-low-as");
    const affirmPrice = document.querySelector(".affirm-ala-price");
    if (affirmPrice && affirmContainer) {
        affirmContainer.style.margin = "40px 0 30px";
    }
}, "3000");

class Accordion {
    constructor() {
        this.accordionHeaders = document.querySelectorAll('.ui-accordion-header');
        this.init();
    }

    init() {
        this.initHeaders();
    }

    initHeaders() {
        this.accordionHeaders.forEach(header => {
            if (!header.querySelector('.ui-accordion-header-icon')) {
                const icon = document.createElement('span');
                icon.className = 'ui-accordion-header-icon';
                header.appendChild(icon);
            }
            header.addEventListener('click', () => {
                const wrapper = header.closest('.wrapper_accordion');
                if (wrapper) {
                    this.toggleAccordion(wrapper);
                }
            });
        });
    }

    toggleAccordion(wrapper) {
        const content = wrapper.querySelector('.ui-accordion-content');
        const isActive = wrapper.classList.toggle('active');

        if (content) {
            if (isActive) {
                content.style.display = 'block';
                const height = content.scrollHeight;
                content.style.maxHeight = `${height}px`;
            } else {
                content.style.maxHeight = 0;
                setTimeout(() => {
                    content.style.display = 'none';
                }, 300);
            }
        }
    }
}

new Accordion();

/**
 * CFA link + Reddit click id handling
 * - pulls product slug + active variant
 * - merges UTMs
 * - appends rdt_cid from URL or cookie
 * - pushes rdt_cid into dataLayer
 * - updates <a id="availability_link"> on load and just before click
 */
document.addEventListener("DOMContentLoaded", function () {
    function getAvailabilityAttributionParams() {
        if (
            window.__sternAttributionApi &&
            typeof window.__sternAttributionApi.getQueryDecoratorParams === "function"
        ) {
            return window.__sternAttributionApi.getQueryDecoratorParams() || {};
        }
        const fallback = getMergedUtm();
        const clickIds = getClickIds();
        return Object.assign({}, fallback, clickIds);
    }

    (function pushRdtToDataLayer() {
        const attributionParams = getAvailabilityAttributionParams();
        const rdtCid = attributionParams.rdt_cid || '';
        if (!rdtCid) return;
        if (window.__sternProductAvailabilityRdtPushed === true) {
            return;
        }
        window.__sternProductAvailabilityRdtPushed = true;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'reddit_click_id',
            rdt_cid: rdtCid
        });
    })();

    function updateAvailabilityLink() {
        const pathParts = window.location.pathname.split("/");
        const productSlug = pathParts[pathParts.length - 1];
        if (!productSlug) return;

        const activeVariant = document.querySelector(".type-product__value.active-value-product");
        const variantName = activeVariant ? activeVariant.textContent.trim() : "";
        const attributionParams = getAvailabilityAttributionParams();
        const availabilityUrl = new URL("https://shop.sternpinball.com/pages/contact-for-availability");
        availabilityUrl.searchParams.set("product", productSlug);
        if (variantName) {
            availabilityUrl.searchParams.set("variant", variantName);
        }
        Object.entries(attributionParams).forEach(([key, value]) => {
            if (!value) return;
            availabilityUrl.searchParams.set(key, String(value));
        });

        const linkElement = document.getElementById("availability_link");
        if (linkElement) {
            linkElement.setAttribute("href", availabilityUrl.toString());
        }
    }

    updateAvailabilityLink();
    setTimeout(updateAvailabilityLink, 500);
    setTimeout(updateAvailabilityLink, 1500);

    document.querySelectorAll(".type-product__value").forEach(element => {
        element.addEventListener("click", function () {
            updateAvailabilityLink();
        });
    });

    window.addEventListener('hashchange', updateAvailabilityLink);
    window.addEventListener('popstate', updateAvailabilityLink);

    // Ensure we patch the href right before navigation on click
    document.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        const link = e.target.closest('#availability_link');
        if (link) {
            updateAvailabilityLink();
        }
    }, true);
});

document.addEventListener('DOMContentLoaded', function () {
    const productWithAddons = document.getElementById('product-with-addons');

    function showNotification(productTitle) {
        const el = document.getElementById('bundle-notification');
        const title = document.getElementById('notification-title');
        if (!el || !title) return;

        title.textContent = productTitle;
        el.classList.remove('hidden');

        // Trigger enter animation
        requestAnimationFrame(() => {
            el.classList.add('show');
        });

        // Auto-hide after 3 seconds
        setTimeout(() => {
            el.classList.remove('show');

            // Wait for the transition to finish, then hide
            setTimeout(() => {
                el.classList.add('hidden');
            }, 500); // match CSS transition duration
        }, 3000);
    }

    if (productWithAddons) {
        const checkboxes = document.querySelectorAll('.addon-checkbox');
        const addToCartBtn = document.getElementById('add-bundle');
        if (!addToCartBtn) return;

        function updateButtonState() {
            const isAnyChecked = Array.from(checkboxes).some(cb => cb.checked);

            if (isAnyChecked) {
                addToCartBtn.disabled = false;
                addToCartBtn.classList.remove('btn:solid[grey]', 'disabled');
                addToCartBtn.classList.add('btn:solid[red]');
                addToCartBtn.style.cursor = 'pointer';
            } else {
                addToCartBtn.disabled = true;
                addToCartBtn.classList.remove('btn:solid[red]');
                addToCartBtn.classList.add('btn:solid[grey]', 'disabled');
                addToCartBtn.style.cursor = 'not-allowed';
            }
        }

        // Initialize button state
        updateButtonState();

        // Attach listeners to checkboxes
        checkboxes.forEach(cb => {
            cb.addEventListener('change', updateButtonState);
        });

        const add_bundle = document.querySelector('#add-bundle');
        const form = document.forms['add_to_cart'];
        if (!add_bundle || !form) return;

        add_bundle.addEventListener("click", function (e) {

            if (add_bundle.classList.contains('disabled')) {
                return; // Stop if the button is disabled
            }
            const mainVariantElement = document.querySelector('.blobselect-selection');
            const mainVariantId = mainVariantElement?.getAttribute('data-value');
            const quantityInput = form.querySelector('#quantity');
            const quantity = quantityInput ? quantityInput.value : 1;

            if (!mainVariantId) {
                // alert("Please select a main product variant.");
                return;
            }

            // Get selected addon IDs
            const addonCheckboxes = form.querySelectorAll('input.addon-checkbox:checked');
            if (!addonCheckboxes || !addonCheckboxes.length) {
                return; //
            }

            let ver = '';
            addonCheckboxes.forEach((checkbox) => {
                ver += checkbox.value
            });

            // Prepare line items
            const items = [
                {
                    id: mainVariantId, // main product
                    quantity: quantity,
                    properties: {
                        bundle_id: mainVariantId, // identifies this bundle group
                        ver: ver
                    }
                }
            ];

            const validateOptions = [];
            addonCheckboxes.forEach((checkbox) => {
                items.push({
                    id: checkbox.value,
                    quantity: quantity,
                    properties: {
                        bundled_with: mainVariantId,
                        ver: ver
                    }
                });

                validateOptions.push(checkbox.value);
            });

            if (!validateOptions.length) {
                return;
            }

            // Send all items to cart
            fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ items })
            })
                .then(response => {
                    if (!response.ok) throw new Error("Cart add failed.");
                    return response.json();
                })
                .then(data => {
                    // console.log('Cart updated:', data);
                    const addedTitle = data && data.items && data.items[0] ? data.items[0].title : 'Bundle added';
                    showNotification(addedTitle);
                    // Optional: redirect to cart or show a popup
                    // window.location.href = '/cart';
                })
                .catch(error => {
                    console.error('Error adding to cart:', error);
                    // alert("Failed to add to cart. Try again.");
                });
        });
    }
});

document.addEventListener('DOMContentLoaded', function () {
    const buttons = document.querySelectorAll('.remove-bundle-btn');

    buttons.forEach(button => {
        button.addEventListener('click', function() {

            const parent = this.closest('tr');
            if (!parent) return;
            const input = parent.querySelector('.quantity'); // find .quantity input inside it

            if (input) {
                input.value = 0;
            }
        });
    });
});

document.addEventListener('DOMContentLoaded', function() {
    const cartForm = document.forms['cart'];

    if (cartForm) {
        cartForm.addEventListener("submit", function (e) {
            const submitter = e.submitter && e.submitter.name ? e.submitter.name : '';
            // Check if the clicked button is the "update" or "remove" button
            if (submitter) {
                if (submitter === 'update' || submitter === 'remove') {
                    const quantityData = cartForm.querySelectorAll('.quantity');
                    const updates = {};

                    quantityData.forEach(quantity => {
                        const currentQuantity = quantity.getAttribute('data-keys');
                        if (currentQuantity) {
                            const currentQuantityData = currentQuantity.split(',');

                            currentQuantityData.forEach(key => {
                                updates[key] = quantity.value;
                            });
                        }
                    });

                    return fetch('/cart/update.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({updates})
                    }).then(data => {
                        console.log('Cart updated:', data);
                        // Optional: redirect to cart or show a popup
                        window.location.reload()
                    })
                }
            }
        });
    }
});